import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Sparkles, Loader2, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TaskStatusBadge } from '@/features/tasks/components/TaskStatusBadge';
import { TaskPriorityBadge } from '@/features/tasks/components/TaskPriorityBadge';
import { TaskForm } from '@/features/tasks/components/TaskForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface TaskDetail {
  id: string;
  title: string;
  description: string;
  status: 'todo' | 'in_progress' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  due_date: string | null;
  project_id: string | null;
  created_at: string;
  updated_at: string;
}

interface SubTask {
  id: string;
  title: string;
  is_completed: boolean;
}

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<string | null>(null);

  const { data: task, isLoading, error, refetch } = useQuery({
    queryKey: ['task', taskId],
    queryFn: async () => {
      const { data, error: fetchError } = await supabase
        .from('task')
        .select('*')
        .eq('id', taskId!)
        .single();
      if (fetchError) throw fetchError;
      return data as unknown as TaskDetail;
    },
    enabled: !!taskId,
  });

  const { data: subTasks } = useQuery({
    queryKey: ['sub-tasks', taskId],
    queryFn: async () => {
      const { data } = await supabase
        .from('sub_task')
        .select('id, title, is_completed')
        .eq('task_id', taskId!)
        .order('created_at');
      return (data ?? []) as unknown as SubTask[];
    },
    enabled: !!taskId,
  });

  const updateTask = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const { error: updateError } = await supabase
        .from('task')
        .update(values as never)
        .eq('id', taskId!);
      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setShowEdit(false);
    },
  });

  const deleteTask = useMutation({
    mutationFn: async () => {
      const { error: delError } = await supabase
        .from('task')
        .update({ deleted_at: new Date().toISOString() } as never)
        .eq('id', taskId!);
      if (delError) throw delError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      navigate('/tasks');
    },
  });

  const getAiSuggestion = useMutation({
    mutationFn: async () => {
      const { data, error: aiError } = await supabase.functions.invoke('task-ai', {
        body: { action: 'suggest', task },
      });
      if (aiError) throw aiError;
      return data.result as string;
    },
    onSuccess: (result) => setAiSuggestion(result),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !task) {
    return <ErrorAlert message={error ? String(error) : 'Task not found'} onRetry={refetch} />;
  }

  return (
    <div>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tasks
        </Button>
      </div>

      <PageHeader
        title={task.title}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowEdit(true)}>Edit</Button>
            <Button variant="destructive" size="icon" onClick={() => deleteTask.mutate()}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Description</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {task.description || 'No description provided.'}
              </p>
            </CardContent>
          </Card>

          {(subTasks ?? []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Sub-tasks</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {subTasks!.map((st) => (
                  <div key={st.id} className="flex items-center justify-between rounded-md border p-2 text-sm">
                    <span className={st.is_completed ? 'line-through text-muted-foreground' : ''}>{st.title}</span>
                    <span className="text-xs text-muted-foreground">{st.is_completed ? 'Done' : 'Pending'}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* AI Suggestion */}
          <Card>
            <CardContent className="pt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => getAiSuggestion.mutate()}
                disabled={getAiSuggestion.isPending}
              >
                {getAiSuggestion.isPending
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Sparkles className="mr-2 h-4 w-4" />}
                AI Suggestions
              </Button>
              {aiSuggestion && (
                <p className="mt-3 text-sm whitespace-pre-wrap">{aiSuggestion}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <TaskStatusBadge status={task.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Priority</span>
                <TaskPriorityBadge priority={task.priority} />
              </div>
              {task.due_date && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Due Date</span>
                  <span>{new Date(task.due_date).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{new Date(task.created_at).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Task</DialogTitle></DialogHeader>
          <TaskForm
            defaultValues={{
              title: task.title,
              description: task.description,
              status: task.status,
              priority: task.priority,
              due_date: task.due_date ?? '',
            }}
            onSubmit={(values) => updateTask.mutate(values)}
            isSubmitting={updateTask.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
